import { Theme, alpha } from '@mui/material'
import { SystemStyleObject } from '@mui/system'
import { advancedColors, onDark, surfaces } from '../theme'
import { CaseloadView } from './caseloadView'

// Selectors into Standard's markup; `AdvancedSidebar.test.tsx` fails when one stops matching.
export const standardHooks = {
  riskChip: '[data-testid="risk-indicator"]',
  daysDisengagedChip: '[data-testid="days-disengaged-indicator"]',
  stalledChip: '[data-testid="stalled-track-indicator"]',
  pendingDocChip: '[data-testid="pending-doc-indicator"]',
  callFollowUpChip: '[data-testid="call-follow-up-indicator"]',
  hqOverdueChip: '[data-testid="hq-overdue-indicator"]',
  unreadChip: '[data-testid="unread-message-indicator"]',
  chip: '[role="img"]',
  filtersButton: 'button[aria-label^="Filters"]',
  toggleButton: '.MuiButton-root[aria-pressed]',
  pressedButton: '.MuiButton-root[aria-pressed="true"]',
  count: '[data-testid$="-count"]',
  reEngageFilter: '[aria-label^="Re-engage"]',
  followUpFilter: '[aria-label^="Follow-up"]',
  onTrackFilter: '[aria-label^="On track"]',
  controlIcon: 'svg path',
} as const

const notChips = (hooks: string[]) => hooks.map((hook) => `:not(${hook})`).join('')

const statusChips = notChips([standardHooks.riskChip, standardHooks.daysDisengagedChip, standardHooks.stalledChip])

// Standard's dark unread chip already reads on the selected row (ADR-128).
const selectedRowChips = notChips([
  standardHooks.riskChip,
  standardHooks.daysDisengagedChip,
  standardHooks.stalledChip,
  standardHooks.unreadChip,
])

export const filterBarSx: SystemStyleObject<Theme> = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  pt: 2.5,
  pb: 2,
  backgroundColor: surfaces.green.bg,
  borderBottom: `1px solid ${surfaces.green.stroke}`,
  boxShadow: (theme) =>
    `0 1px 1px ${alpha(theme.palette.common.black, 0.08)}, 0 4px 10px ${alpha(theme.palette.common.black, 0.1)}`,
  [`& ${standardHooks.filtersButton}`]: {
    borderColor: surfaces.green.stroke,
    '&:hover': { backgroundColor: advancedColors.canvas },
    // The second span is the plus section; MUI's ripple span comes last.
    '& > span:nth-of-type(2)': { borderColor: surfaces.green.stroke },
  },
  [`& ${standardHooks.toggleButton}`]: {
    borderColor: surfaces.green.stroke,
    '&:hover': { backgroundColor: advancedColors.canvas },
  },
  [`& ${standardHooks.pressedButton}`]: {
    backgroundColor: advancedColors.green,
    borderColor: advancedColors.green,
    '&:hover': { backgroundColor: advancedColors.greenHover },
  },
  [`& ${standardHooks.count}`]: {
    backgroundColor: advancedColors.deep,
    color: advancedColors.surface,
    boxShadow: `0 0 0 1px ${surfaces.green.bg}`,
  },
}

export const sideTintSx: SystemStyleObject<Theme> = {
  position: 'relative',
  '&::before, &::after': {
    content: '""',
    pointerEvents: 'none',
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 18,
    zIndex: 2,
  },
  '&::before': {
    left: 0,
    background: (theme) => `linear-gradient(to right, ${alpha(theme.palette.common.black, 0.06)}, transparent)`,
  },
  '&::after': {
    right: 0,
    background: (theme) => `linear-gradient(to left, ${alpha(theme.palette.common.black, 0.06)}, transparent)`,
  },
}

export const filterControlsSx: SystemStyleObject<Theme> = {
  '& .MuiIconButton-root': {
    color: advancedColors.deep,
    '&:hover': { backgroundColor: alpha(advancedColors.deep, 0.08) },
  },
  [`& ${standardHooks.controlIcon}`]: { stroke: advancedColors.deep },
}

export const rowChipsSx = (selected: boolean): SystemStyleObject<Theme> =>
  selected
    ? {
        [`& ${standardHooks.chip}${selectedRowChips}`]: {
          backgroundColor: advancedColors.surface,
          color: advancedColors.deep,
          boxShadow: `inset 0 0 0 1px ${surfaces.green.stroke}`,
        },
      }
    : {
        [`& ${standardHooks.chip}${statusChips}`]: {
          backgroundColor: onDark.surface,
          color: onDark.primary,
          boxShadow: `inset 0 0 0 1px ${onDark.line}`,
        },
        [`& ${standardHooks.chip} img`]: { filter: 'brightness(0) invert(1)' },
        [`& ${standardHooks.unreadChip} text`]: { fill: onDark.surface },
      }

export const filterBarViewSx = (view: CaseloadView): SystemStyleObject<Theme> =>
  view === 'nutritionist'
    ? {
        [`& ${standardHooks.reEngageFilter}, & ${standardHooks.followUpFilter}, & ${standardHooks.onTrackFilter}`]: {
          display: 'none',
        },
      }
    : {}

const therapyChips = [
  standardHooks.daysDisengagedChip,
  standardHooks.pendingDocChip,
  standardHooks.callFollowUpChip,
  standardHooks.hqOverdueChip,
].join(', & ')

export const rowViewSx = (view: CaseloadView): SystemStyleObject<Theme> =>
  view === 'nutritionist' ? { [`& ${therapyChips}`]: { display: 'none' } } : {}
